import { components } from "@/api-schema";
import { formatApiDateOnlyToLocale } from "@/common/helpers/format-date";
import { SystemInfoSubComponent } from "@/components/data-table/predefined/_components/system-info-sub-component";
import { DataTableRow } from "@/components/data-table/types";
import { EmptyValue } from "@/components/ui/empty-value";
import { BuildingIcon, CalendarIcon, DoorOpenIcon, MapPinIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

type Vehicle = components["schemas"]["Vehicle"] & {
  registrationDate?: string | null;
};

function formatRegistrationDate(registrationDate?: string | null): string | null {
  return formatApiDateOnlyToLocale(registrationDate) ?? null;
}

export interface VehicleSubComponentProps {
  row: DataTableRow<Vehicle>;
}

export function VehicleSubComponent({ row }: VehicleSubComponentProps) {
  const { t } = useTranslation(["masterdata", "common"]);
  const vehicle = row.original;
  const owningBranch = vehicle.owningBranch;
  const currentBranch = vehicle.currentBranch;
  const formattedRegistrationDate = formatRegistrationDate(vehicle.registrationDate);

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      <div className="border-border bg-background flex h-fit items-center gap-3 rounded-lg border p-3">
        <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-full">
          <BuildingIcon className="text-primary h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="text-muted-foreground text-xs">{t("common:OwningBranch")}</div>
          <div className="font-semibold">{owningBranch?.name ?? <EmptyValue />}</div>
        </div>
      </div>
      <div className="border-border bg-background flex h-fit items-center gap-3 rounded-lg border p-3">
        <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-full">
          <MapPinIcon className="text-primary h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="text-muted-foreground text-xs">{t("masterdata:CurrentBranch")}</div>
          <div className="font-semibold">{currentBranch?.name ?? <EmptyValue />}</div>
        </div>
      </div>
      <div className="border-border bg-background flex h-fit items-center gap-3 rounded-lg border p-3">
        <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-full">
          <CalendarIcon className="text-primary h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="text-muted-foreground text-xs">{t("common:RegistrationDate")}</div>
          <div className="font-semibold">{formattedRegistrationDate ?? <EmptyValue />}</div>
        </div>
      </div>
      <div className="border-border bg-background flex h-fit items-center gap-3 rounded-lg border p-3">
        <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-full">
          <DoorOpenIcon className="text-primary h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="text-muted-foreground text-xs">{t("common:NumberDoors")}</div>
          <div className="font-semibold">{vehicle.numberDoors ?? <EmptyValue />}</div>
        </div>
      </div>
      <SystemInfoSubComponent uuid={vehicle.uuid} createdAt={vehicle.createdAt} updatedAt={vehicle.updatedAt} />
    </div>
  );
}
